import React, { useMemo, useState } from 'react';
import { Check, Pin, AlertTriangle } from 'lucide-react';
import { knowledgePrioritiesService } from '../services/KnowledgeRulesService';

interface Candidate { partId?: string | null; partName?: string | null; reason?: string | null; }
interface Decision {
    related_corrected_name?: string | null;
    partId?: string | null;
    group_id?: string | null;
    section_id?: string | null;
    probability_score?: number | null;
    resolution_keys?: string[];
    resolutions_explanation?: string[];
    alternatives?: Candidate[];
    fallback_attempt?: number | null;
    fallback_note?: string | null;
}
interface Scope { catalog?: string; language?: string; organization_id?: number | null; }
interface Trace { scope?: Scope; sections?: Record<string, Candidate[]>; decisions?: Decision[]; }

const scoreColor = (s?: number | null) => {
    if (s == null) return 'bg-gray-100 text-gray-600';
    if (s >= 0.9) return 'bg-green-50 text-green-700';
    if (s >= 0.7) return 'bg-yellow-50 text-yellow-700';
    return 'bg-red-50 text-red-700';
};

const PartDecisionsPanel: React.FC<{ state?: Record<string, any> | null }> = ({ state }) => {
    const trace: Trace | null = state?.['_search_decision_trace'] || null;
    const [applyGlobal, setApplyGlobal] = useState(false);
    const [pinned, setPinned] = useState<Record<string, string>>({}); // key -> 'ok' | error

    const scope: Scope = trace?.scope || {};

    const decisions = trace?.decisions || [];
    const hasTrace = !!trace && (decisions.length > 0 || Object.keys(trace?.sections || {}).length > 0);

    const candidatesFor = useMemo(() => (d: Decision): Candidate[] => {
        const secKey = `${d.group_id}/${d.section_id}`;
        const fromSection = trace?.sections?.[secKey] || [];
        const merged: Record<string, Candidate> = {};
        for (const c of fromSection) if (c.partId) merged[c.partId] = { ...merged[c.partId], ...c };
        for (const a of d.alternatives || []) if (a.partId) merged[a.partId] = { ...merged[a.partId], ...a };
        if (d.partId && !merged[d.partId]) merged[d.partId] = { partId: d.partId, partName: d.related_corrected_name };
        return Object.values(merged);
    }, [trace]);

    if (!hasTrace) return null;

    const pinPart = async (d: Decision, c: Candidate) => {
        const key = `${d.related_corrected_name}:${c.partId}`;
        const term = (d.related_corrected_name || '').trim();
        // The new dictionary is term → ordered meanings (catalog name keywords).
        // "Pin this" makes the chosen candidate's catalog NAME the rank-1 meaning.
        const topMeaning = (c.partName || c.partId || '').trim();
        if (!term || !topMeaning) {
            setPinned(p => ({ ...p, [key]: 'missing term/name' }));
            return;
        }
        try {
            await knowledgePrioritiesService.create({
                catalog: scope.catalog || '*',
                language: scope.language || '',
                organization_id: applyGlobal ? null : (scope.organization_id ?? null),
                term,
                meanings: [topMeaning],
                note: `Pinned from part decisions (partId ${c.partId || '?'}).`,
                enabled: true,
            });
            setPinned(p => ({ ...p, [key]: 'ok' }));
        } catch (e: any) {
            setPinned(p => ({ ...p, [key]: e?.response?.data?.detail || 'failed' }));
        }
    };

    return (
        <div className="mb-6 bg-white rounded-lg shadow">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                    <h3 className="text-lg font-medium text-gray-900">Part Decisions</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                        What the search agent chose vs the candidates it weighed.
                        Scope: <span className="font-mono">{scope.catalog || '*'} / {scope.language || '?'} / {scope.organization_id ?? 'global'}</span>
                    </p>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-600">
                    <input type="checkbox" checked={applyGlobal} onChange={e => setApplyGlobal(e.target.checked)} />
                    Pin globally (all orgs)
                </label>
            </div>

            <div className="divide-y divide-gray-100">
                {decisions.map((d, i) => {
                    const cands = candidatesFor(d);
                    return (
                        <div key={i} className="px-6 py-4">
                            <div className="flex items-center justify-between">
                                <div className="font-medium text-gray-900">{d.related_corrected_name || '(unnamed)'}</div>
                                <span className={`px-2 py-0.5 rounded-full text-xs ${scoreColor(d.probability_score)}`}>
                                    {d.probability_score != null ? d.probability_score.toFixed(2) : '—'}
                                </span>
                            </div>
                            {(d.resolution_keys?.length || d.fallback_attempt != null) && (
                                <div className="mt-1 text-xs text-gray-500">
                                    {d.resolution_keys?.join(', ')}
                                    {d.fallback_attempt != null && <span className="ml-2 inline-flex items-center text-amber-600"><AlertTriangle className="w-3 h-3 mr-1" />fallback {d.fallback_attempt}</span>}
                                </div>
                            )}
                            {(d.resolutions_explanation || []).map((ex, j) => (
                                <div key={j} className="mt-1 text-xs text-gray-600 italic">“{ex}”</div>
                            ))}

                            <div className="mt-3 border rounded-lg divide-y divide-gray-50">
                                {cands.map((c, k) => {
                                    const chosen = c.partId === d.partId;
                                    const key = `${d.related_corrected_name}:${c.partId}`;
                                    const status = pinned[key];
                                    return (
                                        <div key={k} className={`flex items-center justify-between px-3 py-2 text-sm ${chosen ? 'bg-blue-50/40' : ''}`}>
                                            <div className="min-w-0">
                                                <span className="font-mono text-xs text-gray-500 mr-2">{c.partId}</span>
                                                <span className="text-gray-800">{c.partName || ''}</span>
                                                {chosen && <span className="ml-2 inline-flex items-center text-blue-600 text-xs"><Check className="w-3 h-3 mr-0.5" />chosen</span>}
                                                {c.reason && <span className="ml-2 text-xs text-gray-400">— {c.reason}</span>}
                                            </div>
                                            <div className="shrink-0">
                                                {status === 'ok' ? (
                                                    <span className="text-xs text-green-600 inline-flex items-center"><Check className="w-3 h-3 mr-1" />pinned</span>
                                                ) : (
                                                    <button
                                                        onClick={() => pinPart(d, c)}
                                                        className="inline-flex items-center px-2 py-1 text-xs border rounded-md text-gray-600 hover:bg-gray-50"
                                                        title="Create a pin rule for this term → this partId"
                                                    >
                                                        <Pin className="w-3 h-3 mr-1" />Pin this
                                                    </button>
                                                )}
                                                {status && status !== 'ok' && <span className="ml-2 text-xs text-red-600">{status}</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                                {cands.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No candidate detail captured.</div>}
                            </div>
                        </div>
                    );
                })}
                {decisions.length === 0 && (
                    <div className="px-6 py-4 text-sm text-gray-400">Candidates were captured but no final selections recorded for this turn.</div>
                )}
            </div>
        </div>
    );
};

export default PartDecisionsPanel;
